import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Wallet, Building2, ArrowUpRight, TrendingUp, TrendingDown, Landmark, Download, ImageDown, FileText, Loader2, Cpu } from "lucide-react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import { ChartTooltip } from "@/components/charts/ChartTooltip";
import { cn } from "@/lib/utils";
import { seriesColor } from "@/lib/chart";
import { EditableText } from "@/components/cms/EditableText";
import { toast } from "sonner";
import { toPng } from "html-to-image";
import jsPDF from "jspdf";

const MONTHS_ORDER = ["Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec", "Jan", "Feb", "Mar"];
const monthsForFy = (fy) => {
  const m = /FY(\d{4})/.exec(fy || "");
  const start = m ? parseInt(m[1], 10) : 2026;
  const yy = (n) => String(n).slice(-2);
  return MONTHS_ORDER.map((mn, i) => `${mn}-${yy(i < 9 ? start : start + 1)}`);
};
const QUARTERS = [
  { value: "Q1", label: "Q1 · Apr–Jun", idx: [0, 3] },
  { value: "Q2", label: "Q2 · Jul–Sep", idx: [3, 6] },
  { value: "Q3", label: "Q3 · Oct–Dec", idx: [6, 9] },
  { value: "Q4", label: "Q4 · Jan–Mar", idx: [9, 12] },
];
const HALVES = [
  { value: "H1", label: "H1 · Apr–Sep", idx: [0, 6] },
  { value: "H2", label: "H2 · Oct–Mar", idx: [6, 12] },
];

const TOP_N = 8;

// Corporate entities shown as their own donut slices (replacing a single
// "Corporate" segment). "Brand" == Sumadhura, matching the Corporate Overview.
const ENTITY_META = {
  Sumadhura: { label: "Brand", color: "hsl(var(--chart-cat-2))", icon: Building2 },
  Signet: { label: "Signet", color: "hsl(var(--chart-cat-5))", icon: Landmark },
  Tech: { label: "Tech", color: "hsl(var(--chart-cat-1))", icon: Cpu },
};
const entityMeta = (name, i) => ENTITY_META[name] || { label: name, color: seriesColor(i + 1), icon: Landmark };

const fyShort = (fy) => {
  const m = /FY(\d{4})-(\d{2})/.exec(fy || "");
  return m ? `FY ${m[1].slice(2)}-${m[2]}` : (fy || "");
};

// Months of the period immediately preceding the current selection (delta baseline).
function prevPeriodMonths(mode, sel, fyMonths) {
  if (mode === "month") {
    const idx = fyMonths.indexOf(sel.month);
    return idx > 0 ? [fyMonths[idx - 1]] : [];
  }
  if (mode === "quarter") {
    const qi = QUARTERS.findIndex((q) => q.value === sel.quarter);
    if (qi <= 0) return [];
    const p = QUARTERS[qi - 1];
    return fyMonths.slice(p.idx[0], p.idx[1]);
  }
  const hi = HALVES.findIndex((h) => h.value === sel.half);
  if (hi <= 0) return [];
  const p = HALVES[hi - 1];
  return fyMonths.slice(p.idx[0], p.idx[1]);
}

/**
 * Consolidated Spends (PART 2 · Section 1)
 *   Left : Total Incurred Amount (focal) + period-over-period delta + context.
 *   Right: By Project Spends — horizontal bar graph, sorted desc, value + % labels.
 * Clicking a project (or "View spends by project") opens a grid of project BOXES,
 * each showing that project's vertical breakdown.
 */
export function ConsolidatedSpends() {
  const { money } = useCurrency();
  const [fys, setFys] = useState(["FY2026-27"]);
  const [year, setYear] = useState("FY2026-27");
  const [mode, setMode] = useState("month"); // month | quarter | half
  const [sel, setSel] = useState({ month: "", quarter: "Q2", half: "H1" });
  const [meta, setMeta] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [focusProject, setFocusProject] = useState("");
  const exportRef = useRef(null);
  const [exporting, setExporting] = useState(false);

  const fyMonths = useMemo(() => monthsForFy(year), [year]);

  const period = useMemo(() => {
    if (mode === "month") return sel.month;
    if (mode === "quarter") {
      const q = QUARTERS.find((x) => x.value === sel.quarter) || QUARTERS[0];
      return fyMonths.slice(q.idx[0], q.idx[1]).join(",");
    }
    const h = HALVES.find((x) => x.value === sel.half) || HALVES[0];
    return fyMonths.slice(h.idx[0], h.idx[1]).join(",");
  }, [mode, sel, fyMonths]);

  useEffect(() => {
    let on = true;
    api.fiscalYears().then((d) => {
      if (!on) return;
      setFys(d.years || ["FY2026-27"]);
      setYear((y) => ((d.years || []).includes(y) ? y : (d.default || (d.years || ["FY2026-27"])[0])));
    }).catch(() => {});
    api.spendsOverview("FY").then((d) => {
      if (!on) return;
      setMeta(d);
      setSel((s) => ({ ...s, month: s.month || d.latest_month }));
    }).catch(() => {});
    return () => { on = false; };
  }, []);

  useEffect(() => {
    setSel((s) => {
      if (!s.month) return s;
      if (fyMonths.includes(s.month)) return s;
      const fallback = (meta?.latest_month && fyMonths.includes(meta.latest_month))
        ? meta.latest_month : fyMonths[fyMonths.length - 1];
      return { ...s, month: fallback };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fyMonths]);

  useEffect(() => {
    if (!period) return;
    let on = true;
    setLoading(true);
    api.spendsOverview(period)
      .then((d) => { if (on) { setData(d); setLoading(false); } })
      .catch(() => on && setLoading(false));
    return () => { on = false; };
  }, [period]);

  const periodLabel = mode === "month" ? sel.month
    : mode === "quarter" ? (QUARTERS.find((q) => q.value === sel.quarter)?.label || sel.quarter)
      : (HALVES.find((h) => h.value === sel.half)?.label || sel.half);
  const modeWord = mode === "month" ? "month" : mode === "quarter" ? "quarter" : "half-year";

  const total = data?.total || 0;
  const projectsFull = data?.by_project_full || [];
  const projCount = projectsFull.length;
  const projTotal = useMemo(() => projectsFull.reduce((s, p) => s + (p.spend || 0), 0), [projectsFull]);
  const maxProjSpend = Math.max(1, ...projectsFull.map((p) => p.spend || 0));
  // Project + per-entity corporate split (complete incurred amount)
  const spendType = data?.by_spend_type || [];
  const projSpend = spendType.find((x) => x.name === "Project")?.spend ?? projTotal;
  const projShare = total ? projSpend / total : 0;
  const corpEntities = data?.by_corporate_entity || [];

  const monthTot = useMemo(() => {
    const src = data?.all_month_totals || meta?.all_month_totals || [];
    return Object.fromEntries(src.map((m) => [m.month, m.spend || 0]));
  }, [data, meta]);
  const prevTotal = useMemo(() => {
    const pm = prevPeriodMonths(mode, sel, fyMonths);
    return pm.reduce((s, m) => s + (monthTot[m] || 0), 0);
  }, [mode, sel, fyMonths, monthTot]);
  const delta = total - prevTotal;
  const deltaPct = prevTotal > 0 ? (delta / prevTotal) * 100 : null;
  const up = delta > 0;

  const openProject = (name) => { setFocusProject(name); setDialogOpen(true); };
  const viewAll = () => { setFocusProject(""); setDialogOpen(true); };

  // Split for the donut + legend: Project + each corporate entity
  // (Brand / Signet / Tech) shown as read-only slices (no drill-down).
  const splitData = [
    { key: "Project", label: "Project", value: projSpend, share: projShare, color: "hsl(var(--primary))", icon: Building2,
      meta: `${projCount} project${projCount === 1 ? "" : "s"}` },
    ...corpEntities.map((e, i) => {
      const m = entityMeta(e.name, i);
      return {
        key: e.name, label: m.label, value: e.spend || 0,
        share: total ? (e.spend || 0) / total : 0, color: m.color, icon: m.icon,
        meta: `${e.count} line item${e.count === 1 ? "" : "s"}`,
      };
    }),
  ];
  const donutData = splitData.filter((d) => d.value > 0);

  // ---- Share Export: one-click PNG / PDF of the panel (client-side, no backend) ----
  const exportBase = () => {
    const p = (periodLabel || "period").replace(/[^\w-]+/g, "-").replace(/^-+|-+$/g, "");
    return `consolidated_spends_${fyShort(year).replace(/\s+/g, "")}_${p}_${new Date().toISOString().slice(0, 10)}`;
  };
  const capturePng = async () => {
    const node = exportRef.current;
    if (!node) return null;
    const bg = getComputedStyle(node).backgroundColor || "#ffffff";
    return toPng(node, {
      cacheBust: true,
      pixelRatio: 2,
      backgroundColor: bg && bg !== "rgba(0, 0, 0, 0)" ? bg : "#ffffff",
      filter: (el) => !(el && el.dataset && el.dataset.exportExclude !== undefined),
    });
  };
  const runExport = async (kind) => {
    if (exporting) return;
    try {
      setExporting(true);
      // let the dropdown/portal close before capturing
      await new Promise((r) => setTimeout(r, 250));
      const dataUrl = await capturePng();
      if (!dataUrl) throw new Error("no-node");
      if (kind === "png") {
        const a = document.createElement("a");
        a.href = dataUrl; a.download = `${exportBase()}.png`;
        document.body.appendChild(a); a.click(); a.remove();
      } else {
        const img = new Image();
        img.src = dataUrl;
        await new Promise((res, rej) => { img.onload = res; img.onerror = rej; });
        const w = img.width, h = img.height;
        const pdf = new jsPDF({ orientation: w >= h ? "landscape" : "portrait", unit: "px", format: [w, h] });
        pdf.addImage(dataUrl, "PNG", 0, 0, w, h);
        pdf.save(`${exportBase()}.pdf`);
      }
      toast.success(`Exported ${kind.toUpperCase()} \u00b7 ready for your deck`);
    } catch (e) {
      toast.error("Could not export the panel. Please try again.");
    } finally {
      setExporting(false);
    }
  };
  const exportMenu = (
    <div data-export-exclude>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="h-9 gap-1.5" disabled={exporting || loading || total === 0} data-testid="consolidated-export-btn">
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            <span className="hidden sm:inline">Export</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" data-testid="consolidated-export-menu">
          <DropdownMenuItem onClick={() => runExport("png")} data-testid="consolidated-export-png">
            <ImageDown className="mr-2 h-4 w-4" /> PNG image
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => runExport("pdf")} data-testid="consolidated-export-pdf">
            <FileText className="mr-2 h-4 w-4" /> PDF document
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );

  const controls = (
    <div className="flex flex-wrap items-center gap-2" data-testid="consolidated-controls">
      <Select value={year} onValueChange={setYear}>
        <SelectTrigger className="h-9 w-[140px]" data-testid="consolidated-year-select"><SelectValue placeholder="Year" /></SelectTrigger>
        <SelectContent>{fys.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
      </Select>
      <ToggleGroup type="single" value={mode} onValueChange={(v) => v && setMode(v)}
        className="rounded-lg border bg-[hsl(var(--surface-2))] p-1" data-testid="consolidated-mode-tabs">
        <ToggleGroupItem value="month" data-testid="consolidated-mode-month" className="h-7 px-2.5 text-xs data-[state=on]:bg-card data-[state=on]:shadow-sm">Month</ToggleGroupItem>
        <ToggleGroupItem value="quarter" data-testid="consolidated-mode-quarter" className="h-7 px-2.5 text-xs data-[state=on]:bg-card data-[state=on]:shadow-sm">Quarter</ToggleGroupItem>
        <ToggleGroupItem value="half" data-testid="consolidated-mode-half" className="h-7 px-2.5 text-xs data-[state=on]:bg-card data-[state=on]:shadow-sm">Half-year</ToggleGroupItem>
      </ToggleGroup>
      {mode === "month" && (
        <Select value={sel.month} onValueChange={(v) => setSel((s) => ({ ...s, month: v }))}>
          <SelectTrigger className="h-9 w-[130px]" data-testid="consolidated-month-select"><SelectValue placeholder="Select month" /></SelectTrigger>
          <SelectContent className="max-h-72">{fyMonths.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
        </Select>
      )}
      {mode === "quarter" && (
        <Select value={sel.quarter} onValueChange={(v) => setSel((s) => ({ ...s, quarter: v }))}>
          <SelectTrigger className="h-9 w-[160px]" data-testid="consolidated-quarter-select"><SelectValue /></SelectTrigger>
          <SelectContent>{QUARTERS.map((q) => <SelectItem key={q.value} value={q.value}>{q.label}</SelectItem>)}</SelectContent>
        </Select>
      )}
      {mode === "half" && (
        <Select value={sel.half} onValueChange={(v) => setSel((s) => ({ ...s, half: v }))}>
          <SelectTrigger className="h-9 w-[160px]" data-testid="consolidated-half-select"><SelectValue /></SelectTrigger>
          <SelectContent>{HALVES.map((h) => <SelectItem key={h.value} value={h.value}>{h.label}</SelectItem>)}</SelectContent>
        </Select>
      )}
    </div>
  );

  return (
    <section ref={exportRef} className="relative overflow-hidden rounded-2xl border bg-card p-5 card-shadow" data-testid="consolidated-spends">
      <span className="pointer-events-none absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-[hsl(var(--primary))] to-[hsl(var(--primary)/0.35)]" />

      {/* Header toolbar */}
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[hsl(var(--primary)/0.12)] text-[hsl(var(--primary))]">
            <Wallet className="h-5 w-5" />
          </span>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="font-heading text-base font-semibold leading-tight"><EditableText cmsKey="consolidated.title" fallback="Consolidated Spends" /></h2>
              <span className="rounded-full border border-[hsl(var(--primary)/0.3)] bg-[hsl(var(--primary)/0.08)] px-2 py-0.5 text-[10px] font-medium text-[hsl(var(--primary))]">Summary</span>
            </div>
            <p className="text-xs text-muted-foreground">Complete spend (project + corporate) by period · {fyShort(year)} · {periodLabel || "—"}</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {controls}
          {exportMenu}
        </div>
      </div>

      {loading && !data ? (
        <div className="mt-5 grid grid-cols-1 gap-6 xl:grid-cols-[2fr_3fr]"><Skeleton className="h-52" /><Skeleton className="h-52" /></div>
      ) : total === 0 ? (
        <div className="mt-6 rounded-xl border border-dashed p-6 text-center text-sm text-muted-foreground" data-testid="consolidated-empty">
          No spends recorded for {fyShort(year)} · {periodLabel}.
        </div>
      ) : (
        <div className="mt-5 grid grid-cols-1 gap-6 xl:grid-cols-[3fr_2fr]" data-testid="consolidated-zones">
          {/* Total Incurred focal with donut split — sits on the RIGHT on desktop */}
          <div className="flex flex-col justify-center border-b pb-6 xl:order-2 xl:border-b-0 xl:border-l xl:pb-0 xl:pl-6" data-testid="consolidated-total-card">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"><EditableText cmsKey="consolidated.total_label" fallback="Total Incurred Amount" /></p>

            {/* Donut (Project vs Corporate) + interactive legend */}
            <div className="mt-3 flex items-center gap-4 sm:gap-5">
              <div className="relative shrink-0" style={{ width: 138, height: 138 }} data-testid="consolidated-donut">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={donutData} dataKey="value" nameKey="key" cx="50%" cy="50%"
                      innerRadius={47} outerRadius={65} paddingAngle={donutData.length > 1 ? 2 : 0} stroke="none"
                      startAngle={90} endAngle={-270} isAnimationActive={false}>
                      {donutData.map((e) => <Cell key={e.key} fill={e.color} />)}
                    </Pie>
                    <Tooltip content={<ChartTooltip formatter={(v) => money(v)} />} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">Total</span>
                  <span className="font-heading text-lg font-bold leading-tight tabular-nums text-[hsl(var(--primary))]" data-testid="consolidated-total">{money(total)}</span>
                </div>
              </div>

              <div className="flex flex-1 flex-col justify-center gap-2" data-testid="consolidated-split">
                {splitData.map((seg) => {
                  const Icon = seg.icon;
                  const testid = `consolidated-split-${String(seg.key).toLowerCase()}`;
                  return (
                    <div key={seg.key} data-testid={testid}
                      style={{ borderLeftColor: seg.color, borderLeftWidth: 3 }}
                      className="flex items-center justify-between gap-2 rounded-lg border bg-card px-2.5 py-2 text-left">
                      <span className="flex min-w-0 items-center gap-2">
                        <Icon className="h-4 w-4 shrink-0" style={{ color: seg.color }} />
                        <span className="min-w-0">
                          <span className="block text-xs font-semibold leading-tight">{seg.label}</span>
                          <span className="block truncate text-[10px] leading-tight text-muted-foreground">{seg.meta}</span>
                        </span>
                      </span>
                      <span className="text-right">
                        <span className="block text-sm font-bold leading-tight tabular-nums">{money(seg.value)}</span>
                        <span className="block text-[10px] font-medium leading-tight tabular-nums" style={{ color: seg.color }}>{Math.round(seg.share * 100)}%</span>
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-2">
              {prevTotal > 0 ? (
                <span className={cn("inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold",
                  up ? "bg-[hsl(var(--warning)/0.14)] text-[hsl(var(--warning))]" : "bg-[hsl(var(--success)/0.14)] text-[hsl(var(--success))]")}
                  data-testid="consolidated-delta">
                  {up ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}
                  {up ? "+" : "−"}{money(Math.abs(delta))}
                  {deltaPct != null && <span className="opacity-80">({up ? "+" : "−"}{Math.abs(deltaPct).toFixed(1)}%)</span>}
                </span>
              ) : (
                <span className="text-xs text-muted-foreground" data-testid="consolidated-delta-none">No prior period to compare</span>
              )}
              <span className="text-[11px] text-muted-foreground">vs previous {modeWord}</span>
            </div>
            <span className="mt-3 inline-flex w-fit rounded-full border bg-[hsl(var(--surface-2))] px-2.5 py-0.5 text-[11px] font-medium text-muted-foreground" data-testid="consolidated-context">
              All spends · Project + Corporate · {fyShort(year)} · {periodLabel}
            </span>
            <button type="button" onClick={viewAll}
              className="mt-4 inline-flex w-fit items-center gap-1 text-xs font-medium text-[hsl(var(--primary))] transition-colors duration-150 hover:underline"
              data-testid="consolidated-view-projects">
              View spends by project <ArrowUpRight className="h-3 w-3" />
            </button>
          </div>

          {/* By Project Spends graph — sits on the LEFT on desktop */}
          <div className="min-w-0 xl:order-1" data-testid="consolidated-projects">
            <div className="mb-2 flex items-center justify-between">
              <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                <Building2 className="h-3.5 w-3.5" /> By Project Spends
              </p>
              <span className="text-[10px] text-muted-foreground">{money(projSpend)} · {projCount} project{projCount === 1 ? "" : "s"}</span>
            </div>
            {projCount === 0 ? (
              <p className="rounded-lg border border-dashed p-4 text-center text-xs text-muted-foreground">No project-attributed spend this period.</p>
            ) : (
              <ScrollArea className="pr-2" style={{ maxHeight: 268 }}>
                <div className="space-y-1.5" data-testid="consolidated-by-project">
                  {projectsFull.map((p, i) => {
                    const pct = projTotal ? Math.round((p.spend / projTotal) * 100) : 0;
                    const barPct = Math.max(3, Math.round((p.spend / maxProjSpend) * 100));
                    const accent = seriesColor(i);
                    return (
                      <button type="button" key={p.name} onClick={() => openProject(p.name)}
                        className="group flex w-full items-center gap-2.5 rounded-md px-1.5 py-1 text-left transition-colors duration-150 hover:bg-[hsl(var(--surface-2))]"
                        data-testid={`consolidated-project-${p.name}`} title={`${p.name} — ${money(p.spend)} (${pct}%)`}>
                        <span className="flex w-28 shrink-0 items-center gap-1.5 text-xs font-medium">
                          <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: accent }} />
                          <span className="truncate">{p.name}</span>
                        </span>
                        <span className="relative block h-5 flex-1 overflow-hidden rounded bg-[hsl(var(--surface-3))]">
                          <span className="block h-full rounded transition-[width] duration-300 group-hover:opacity-90" style={{ width: `${barPct}%`, background: accent }} />
                          <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[10px] font-semibold tabular-nums text-foreground/85">{money(p.spend)}</span>
                        </span>
                        <span className="w-8 shrink-0 text-right text-[11px] text-muted-foreground tabular-nums">{pct}%</span>
                      </button>
                    );
                  })}
                </div>
              </ScrollArea>
            )}
          </div>
        </div>
      )}

      {/* Per-project breakdown — box grid, each box shows its vertical split */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-5xl" data-testid="consolidated-project-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-[hsl(var(--primary))]" /> Spends by Project
            </DialogTitle>
            <DialogDescription>
              {fyShort(year)} · {periodLabel} — {money(projTotal)} project spend across {projCount} project(s). Each box breaks the project down by vertical.
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[66vh]">
            {projCount === 0 ? (
              <p className="p-6 text-center text-sm text-muted-foreground">No project spend for this period.</p>
            ) : (
              <div className="grid grid-cols-1 gap-3 p-1 sm:grid-cols-2 lg:grid-cols-3" data-testid="consolidated-project-boxes">
                {projectsFull.map((p, i) => {
                  const pct = projTotal ? Math.round((p.spend / projTotal) * 100) : 0;
                  const accent = seriesColor(i);
                  const focused = p.name === focusProject;
                  return (
                    <div key={p.name}
                      className={cn("relative overflow-hidden rounded-xl border bg-card p-4 card-shadow transition-shadow duration-150",
                        focused && "ring-2 ring-[hsl(var(--primary))]")}
                      data-testid={`consolidated-project-box-${p.name}`}>
                      <span className="absolute left-0 top-0 h-full w-1.5" style={{ background: accent }} />
                      <div className="flex items-start justify-between gap-2 pl-1.5">
                        <p className="truncate text-sm font-semibold" title={p.name}>{p.name}</p>
                        <span className="shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold tabular-nums" style={{ background: `${accent}22`, color: accent }}>{pct}%</span>
                      </div>
                      <p className="mt-1.5 pl-1.5 font-heading text-xl font-bold tabular-nums" style={{ color: accent }}>{money(p.spend)}</p>
                      <div className="mt-3 space-y-1.5 border-t pt-2.5 pl-1.5" data-testid={`consolidated-verticals-${p.name}`}>
                        {(p.verticals || []).length === 0 ? (
                          <p className="text-[11px] text-muted-foreground">No vertical detail.</p>
                        ) : (
                          (p.verticals || []).map((v) => {
                            const vpct = Math.round((v.share || 0) * 100);
                            return (
                              <div key={v.name} className="flex items-center gap-2">
                                <span className="w-24 shrink-0 truncate text-[11px] text-muted-foreground" title={v.label || v.name}>{v.label || v.name}</span>
                                <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-[hsl(var(--surface-3))]">
                                  <div className="h-full rounded-full" style={{ width: `${Math.max(3, vpct)}%`, background: accent }} />
                                </div>
                                <span className="shrink-0 text-[11px] font-medium tabular-nums">{money(v.spend)}</span>
                                <span className="w-7 shrink-0 text-right text-[10px] text-muted-foreground tabular-nums">{vpct}%</span>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </section>
  );
}
